import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const SuperAdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, isAuthenticated, isLoading } = useSuperAdminAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const result = await login(formData);
      if (result.success) {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors || {});
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Login failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  if (isLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading...</span>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Panel */}
        <div className="left-panel">
          <div className="brand-section">
            <div className="logo-box">
              <i className="fas fa-shield-alt"></i>
            </div>
            <h1>SportsTMS PLATFORM</h1>
            <p>Admin Portal</p>
          </div>

          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-building"></i>
              <span>Manage Tenants</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-globe"></i>
              <span>Domain Management</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-chart-line"></i>
              <span>Analytics</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-cogs"></i>
              <span>System Settings</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="right-panel">
          <div className="form-section">
            <h2>Welcome Back</h2>
            <p>Sign in to access your admin dashboard</p>

            <form onSubmit={handleSubmit}>
              {errors.general && (
                <div className="error-alert">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{errors.general}</span>
                </div>
              )}

              <div className="input-group">
                <label>
                  <i className="fas fa-user"></i> Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                {errors.email && <span className="field-error">{errors.email[0]}</span>}
              </div>

              <div className="input-group">
                <label>
                  <i className="fas fa-lock"></i> Password
                </label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`fas fa-eye${showPassword ? '-slash' : ''}`}></i>
                  </button>
                </div>
                {errors.password && <span className="field-error">{errors.password[0]}</span>}
              </div>

              <div className="form-options">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <a href="/super-admin/forgot-password" className="forgot-link">
                  Forgot Password?
                </a>
              </div>

              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="help-text">
              Need help? <a href="/contact">Contact Support</a>
            </div>
          </div>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .login-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    padding: 20px;
  }

  .login-container {
    display: flex;
    width: 100%;
    max-width: 900px;
    min-height: 550px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }

  /* Left Panel */
  .left-panel {
    flex: 1;
    background: linear-gradient(135deg, #ed3b4b 0%, #4d3b91 100%);
    padding: 40px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    position: relative;
    overflow: hidden;
  }

  .left-panel::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }

  .brand-section {
    text-align: center;
    position: relative;
    z-index: 1;
    margin-bottom: 40px;
  }

  .logo-box {
    width: 90px;
    height: 90px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .logo-box i {
    font-size: 42px;
    color: white;
  }

  .brand-section h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 8px 0;
    letter-spacing: 1px;
    color: white;
  }

  .brand-section p {
    font-size: 1rem;
    margin: 0;
    color: white;
  }

  .features-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
    max-width: 280px;
    position: relative;
    z-index: 1;
  }

  .feature-item {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 16px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.85rem;
    font-weight: 500;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
  }

  .feature-item:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
  }

  .feature-item i {
    font-size: 1.1rem;
    width: 20px;
    text-align: center;
  }

  /* Right Panel */
  .right-panel {
    flex: 1;
    background: white;
    padding: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .form-section {
    width: 100%;
    max-width: 320px;
  }

  .form-section h2 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    text-align: center;
  }

  .form-section > p {
    color: #64748b;
    text-align: center;
    margin: 0 0 28px 0;
    font-size: 0.95rem;
  }

  .error-alert {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 14px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.875rem;
    margin-bottom: 20px;
  }

  .input-group {
    margin-bottom: 20px;
  }

  .input-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 8px;
  }

  .input-group label i {
    color: #ed3b4b;
    margin-right: 6px;
    width: 16px;
  }

  .input-group input {
    width: 100%;
    padding: 12px 14px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: #f8fafc;
  }

  .input-group input:focus {
    outline: none;
    border-color: #ed3b4b;
    background: white;
    box-shadow: 0 0 0 3px rgba(237, 59, 75, 0.1);
  }

  .input-group input::placeholder {
    color: #94a3b8;
  }

  .password-wrapper {
    position: relative;
  }

  .password-wrapper input {
    padding-right: 45px;
  }

  .toggle-password {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    font-size: 1rem;
  }

  .toggle-password:hover {
    color: #ed3b4b;
  }

  .field-error {
    color: #dc2626;
    font-size: 0.75rem;
    margin-top: 4px;
    display: block;
  }

  .form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.875rem;
    color: #64748b;
  }

  .checkbox-wrapper input {
    width: 16px;
    height: 16px;
    accent-color: #ed3b4b;
    cursor: pointer;
  }

  .forgot-link {
    color: #ed3b4b;
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
  }

  .forgot-link:hover {
    text-decoration: underline;
  }

  .submit-btn {
    width: 100%;
    padding: 14px 20px;
    background: linear-gradient(135deg, #ed3b4b 0%, #4d3b91 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(237, 59, 75, 0.35);
  }

  .submit-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #d63344 0%, #3d2e75 100%);
    box-shadow: 0 6px 20px rgba(237, 59, 75, 0.45);
    transform: translateY(-2px);
  }

  .submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .help-text {
    text-align: center;
    margin-top: 24px;
    font-size: 0.875rem;
    color: #64748b;
  }

  .help-text a {
    color: #ed3b4b;
    font-weight: 500;
    text-decoration: none;
  }

  .help-text a:hover {
    text-decoration: underline;
  }

  .loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px;
  }

  .loading-spinner i {
    font-size: 2.5rem;
    color: #ed3b4b;
  }

  .loading-spinner span {
    color: #64748b;
  }

  @media (max-width: 768px) {
    .login-container {
      flex-direction: column;
      max-width: 400px;
    }

    .left-panel {
      padding: 30px 20px;
    }

    .brand-section {
      margin-bottom: 24px;
    }

    .logo-box {
      width: 70px;
      height: 70px;
    }

    .logo-box i {
      font-size: 32px;
    }

    .brand-section h1 {
      font-size: 1.25rem;
    }

    .features-grid {
      max-width: 100%;
    }

    .feature-item {
      padding: 12px 10px;
      font-size: 0.8rem;
    }

    .right-panel {
      padding: 30px 20px;
    }
  }
`;

export default SuperAdminLogin;
